import { useIntl } from 'react-intl';
import { useCallback, useMemo } from 'react';

import { OfferActivateStartType, useRudderStack } from '@ocx/data-analytics';
import { PurchaseVoucherOffer } from '@ocx/data-offers';
import { useConfirmationActionSheet } from '@ocx/feat-modals';
import { useEditProfilePopup } from '@ocx/feat-edit-profile';
import { useOfferPurchaseMutation } from '@ocx/graphql';
// eslint-disable-next-line @nx/enforce-module-boundaries -- app configuration
import { useConfiguration } from '@ocx-app/modules/configuration/useConfiguration';
// eslint-disable-next-line @nx/enforce-module-boundaries -- app logger
import { logInDev } from '@ocx-app/lib/logger/logger';

import { getVoucherExpiresIn, getVoucherExpiresInMessage } from './offer-voucher-expiration.utils';
import { redeemMessages } from './redeem.messages';

type RedeemOfferContext = {
  membershipIsCompleted: boolean;
};
type UseRedeemOffer = (params: { onError: (error: unknown) => void; onCompleted?: () => void }) => {
  redeem: (offer: PurchaseVoucherOffer, context: RedeemOfferContext) => void;
  redeeming: boolean;
};

export const useRedeemOffer: UseRedeemOffer = ({ onError, onCompleted }) => {
  const { triggerEvent } = useRudderStack();
  const intl = useIntl();
  const { open: openEditProfilePopup } = useEditProfilePopup();
  const { open: openConfirmationActionSheet } = useConfirmationActionSheet();
  const redeemOfferWithCompletedProfileOnly = useConfiguration('loyaltyProgram.redeemOfferWithCompletedProfileOnly');
  const [createOfferPurchase, { loading: redeeming }] = useOfferPurchaseMutation({
    refetchQueries: ['currentCustomer', 'offers', 'totalVouchersCount'],
    awaitRefetchQueries: true,
    onError,
    onCompleted,
  });

  const requestMembershipComplete = useCallback(
    () =>
      openConfirmationActionSheet({
        title: intl.formatMessage(redeemMessages.completeProfileTitle),
        description: intl.formatMessage(redeemMessages.completeProfileDescription),
        confirmButtonText: intl.formatMessage(redeemMessages.completeProfileConfirmButton),
        onConfirm: openEditProfilePopup,
      }),
    [intl, openConfirmationActionSheet, openEditProfilePopup],
  );

  const purchaseOffer = useCallback(
    (offer: PurchaseVoucherOffer) => {
      const onPurchaseConfirmed = () => {
        triggerEvent('offer_activate_start', { type: OfferActivateStartType.Redeem, uuid: offer.id });
        createOfferPurchase({
          variables: {
            input: {
              count: 1,
              offerId: offer.id,
            },
          },
        }).catch(logInDev);
      };

      const expiresIn = getVoucherExpiresIn({
        voucherExpiresAt: offer.earningMechanism.award.voucherExpirationDays,
        offerExpiresAt: offer.expireAt,
      });
      const confirmationPopupDescription =
        expiresIn === null
          ? intl.formatMessage(redeemMessages.redeemPointsNoExpirationDaysDescription)
          : intl.formatMessage(redeemMessages.redeemPointsDescription, {
              expiresIn: getVoucherExpiresInMessage(expiresIn, intl),
            });
      triggerEvent('offer_redeem_confirm_requested', { uuid: offer.id });
      openConfirmationActionSheet({
        title: intl.formatMessage(redeemMessages.redeemPointsTitle),
        description: confirmationPopupDescription,
        confirmButtonText: intl.formatMessage(redeemMessages.redeemPointsConfirmButton),
        onConfirm: onPurchaseConfirmed,
      }).catch(logInDev);
    },
    [createOfferPurchase, intl, openConfirmationActionSheet, triggerEvent],
  );

  const redeemOffer = useCallback(
    (offer: PurchaseVoucherOffer, { membershipIsCompleted }: RedeemOfferContext) => {
      const isCompleteProfileRequired =
        offer.customData?.completeProfileRestricted !== undefined
          ? offer.customData.completeProfileRestricted
          : redeemOfferWithCompletedProfileOnly;
      if (isCompleteProfileRequired && !membershipIsCompleted) {
        requestMembershipComplete();
        return;
      }

      purchaseOffer(offer);
    },
    [purchaseOffer, redeemOfferWithCompletedProfileOnly, requestMembershipComplete],
  );

  return useMemo(
    () => ({
      redeem: redeemOffer,
      redeeming,
    }),
    [redeemOffer, redeeming],
  );
};
